import {
  FormControl,
  FormLabel,
  Select,
  FormErrorMessage,
  Input,
  Flex,
  Spinner,
} from "@chakra-ui/react";
import { Field, FormikProps } from "formik";
import { FormValues } from "@/models/proposalForm.model";
import { useEffect, useState } from "react";
import { getBanks } from "@/services/bank.service";

interface BankPaymentFieldsProps {
  formik: FormikProps<FormValues["bankData"]>;
}

const BankPaymentFields = ({ formik }: BankPaymentFieldsProps) => {
  const [banks, setBanks] = useState<Array<{ id: string; name: string }>>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchBanks = async () => {
      try {
        const response = await getBanks(1, 22);
        setBanks(
          response.data.map((bank) => ({ id: bank.id, name: bank.name }))
        );
      } catch (error) {
      } finally {
        setIsLoading(false);
      }
    };

    fetchBanks();
  }, []);

  const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    return e.target.value.replace(/\D/g, "");
  };

  return (
    <>
      <FormControl
        isInvalid={
          !!formik.errors?.accountType && !!formik.touched?.accountType
        }
        mb={6}
      >
        <FormLabel sx={{ fontFamily: "Lexend Deca" }} htmlFor="accountType">
          Tipo de Conta
        </FormLabel>
        <Select
          id="accountType"
          name="accountType"
          placeholder="Selecione o tipo de conta"
          onChange={(e) => formik.setFieldValue("accountType", e.target.value)}
          value={formik.values.accountType}
          variant="flushed"
          sx={{
            fontFamily: "Lexend Deca",
            fontSize: { base: "xl", md: "17px" },
          }}
        >
          <option value="CHECKING_ACCOUNT">Conta Corrente</option>
          <option value="SAVINGS_ACCOUNT">Conta Poupança</option>
        </Select>
        <FormErrorMessage>{formik.errors?.accountType}</FormErrorMessage>
      </FormControl>

      <FormControl
        isInvalid={!!formik.errors?.agency && !!formik.touched?.agency}
        mb={6}
      >
        <FormLabel
          sx={{
            fontFamily: "Lexend Deca",
            fontSize: { base: "xl", md: "17px" },
          }}
          htmlFor="bankCode"
        >
          Banco
        </FormLabel>
        <Flex alignItems="center">
          <Select
            id="bankCode"
            name="bankCode"
            placeholder={
              isLoading ? "Carregando bancos..." : "Selecione o banco"
            }
            onChange={(e) => formik.setFieldValue("bankCode", e.target.value)}
            value={formik.values.bankCode}
            variant="flushed"
            sx={{
              fontFamily: "Lexend Deca",
              fontSize: { base: "xl", md: "17px" },
            }}
            isDisabled={isLoading}
          >
            {!isLoading &&
              banks.map((bank) => (
                <option key={bank.id} value={bank.id}>
                  {bank.name}
                </option>
              ))}
          </Select>
          {isLoading && <Spinner ml={4} color="secondary" />}
        </Flex>
        <FormErrorMessage>{formik.errors?.bankCode}</FormErrorMessage>
      </FormControl>

      <FormControl
        isInvalid={!!formik.errors?.agency && !!formik.touched?.agency}
        mb={6}
      >
        <FormLabel
          sx={{
            fontFamily: "Lexend Deca",
            fontSize: { base: "xl", md: "17px" },
          }}
          htmlFor="agency"
        >
          Agência
        </FormLabel>
        <Field
          as={Input}
          id="agency"
          name="agency"
          placeholder="Digite a agência"
          type="tel"
          w="100%"
          variant="flushed"
          value={formik.values.agency}
          maxLength={4}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            formik.setFieldValue("agency", handleNumberChange(e))
          }
          sx={{
            fontFamily: "Lexend Deca",
            fontSize: { base: "xl", md: "17px" },
          }}
        />
        <FormErrorMessage>{formik.errors?.agency}</FormErrorMessage>
      </FormControl>

      <FormControl
        isInvalid={!!formik.errors?.account && !!formik.touched?.account}
        mb={6}
      >
        <FormLabel
          sx={{
            fontFamily: "Lexend Deca",
            fontSize: { base: "xl", md: "17px" },
          }}
          htmlFor="account"
        >
          Número da Conta
        </FormLabel>
        <Field
          as={Input}
          id="account"
          name="account"
          placeholder="Digite o número da conta"
          type="tel"
          w="100%"
          variant="flushed"
          maxLength={20}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            formik.setFieldValue("account", handleNumberChange(e))
          }
          sx={{
            fontFamily: "Lexend Deca",
            fontSize: { base: "xl", md: "17px" },
          }}
        />
        <FormErrorMessage>{formik.errors?.account}</FormErrorMessage>
      </FormControl>

      <FormControl
        isInvalid={!!formik.errors?.digit && !!formik.touched?.digit}
        mb={4}
      >
        <FormLabel
          sx={{
            fontFamily: "Lexend Deca",
            fontSize: { base: "xl", md: "17px" },
          }}
          htmlFor="digit"
        >
          Dígito
        </FormLabel>
        <Field
          as={Input}
          id="digit"
          name="digit"
          placeholder="Digite o dígito da conta"
          type="tel"
          w="100%"
          variant="flushed"
          maxLength={1}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            formik.setFieldValue("digit", handleNumberChange(e))
          }
          sx={{
            fontFamily: "Lexend Deca",
            fontSize: { base: "xl", md: "17px" },
          }}
        />
        <FormErrorMessage>{formik.errors?.digit}</FormErrorMessage>
      </FormControl>
    </>
  );
};

export default BankPaymentFields;
